"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DATABASE_SHARD_MANIFEST_PROP = void 0;
exports.databaseShardDocWriteClocks = databaseShardDocWriteClocks;
exports.markDatabaseShardCondenseDue = markDatabaseShardCondenseDue;
exports.planDatabaseShardManifestChanges = planDatabaseShardManifestChanges;
exports.sameDatabaseBytes = sameDatabaseBytes;
exports.databaseShardManifestEntryFields = databaseShardManifestEntryFields;
exports.databaseShardManifestEntryToYMap = databaseShardManifestEntryToYMap;
exports.applyDatabaseShardManifestChanges = applyDatabaseShardManifestChanges;
const yjs_1 = require("yjs");
const database_row_fingerprint_1 = require("./database-row-fingerprint");
const database_row_shard_planning_1 = require("./database-row-shard-planning");
const database_value_summary_1 = require("./database-value-summary");
const database_document_scheme_1 = require("./generated/database-document-scheme");
const index_1 = require("./index");
/**
 * The clock the own client of a shard document reached: a document whose
 * state vector reaches it holds everything this client wrote into it.
 */
function databaseShardDocWriteClocks(doc) {
    return new Map([
        [
            doc.clientID,
            (0, yjs_1.decodeStateVector)((0, yjs_1.encodeStateVector)(doc)).get(doc.clientID) ?? 0,
        ],
    ]);
}
/**
 * Asks the server follow-up of the persisted shard document to condense the
 * row fingerprints of its manifest entry (databaseShardRowWriteCondenseDue).
 */
function markDatabaseShardCondenseDue(doc) {
    const condense = doc.getMap(database_document_scheme_1.DATABASE_ROW_SHARD_CONDENSE_MAP_KEY);
    if (condense.get(database_document_scheme_1.DATABASE_ROW_SHARD_CONDENSE_DUE_FIELD) !== true)
        condense.set(database_document_scheme_1.DATABASE_ROW_SHARD_CONDENSE_DUE_FIELD, true);
}
/** Database block property holding the row-shard directory head. */
exports.DATABASE_SHARD_MANIFEST_PROP = 'prop:databaseShardManifest';
const ENTRY_SCALAR_KEYS = [
    'shardId',
    'serverDocId',
    'firstRowId',
    'lastRowId',
    'rowCount',
    'encodedBytes',
    'status',
    'rowFingerprintSeq',
];
/**
 * Minimal ordered changes from `current` to `next`. Entry indexes refer to the
 * shard list after the preceding `shards` change has been applied.
 */
function planDatabaseShardManifestChanges(current, next) {
    (0, index_1.assertDatabaseShardManifest)(next);
    const changes = [];
    const { shards: currentShards, ...currentFields } = current;
    const { shards: nextShards, ...nextFields } = next;
    for (const [key, value] of Object.entries(nextFields))
        if (currentFields[key] !== value)
            changes.push({ kind: 'field', key, value });
    const order = (0, database_row_shard_planning_1.planDatabaseRowOrderChange)(currentShards.map(shard => shard.shardId), nextShards.map(shard => shard.shardId));
    if (order.deleteCount || order.insert.length)
        changes.push({
            kind: 'shards',
            index: order.index,
            deleteCount: order.deleteCount,
            insert: nextShards.slice(order.index, order.index + order.insert.length),
        });
    // A shard stored twice keeps its first entry; the order change above
    // removed the second.
    const previous = new Map([...currentShards].reverse().map(shard => [shard.shardId, shard]));
    const inserted = new Set(order.insert);
    nextShards.forEach((entry, index) => {
        if (inserted.has(entry.shardId))
            return;
        const before = previous.get(entry.shardId);
        // An entry read once and passed on as read has nothing to compare.
        if (before === entry)
            return;
        for (const key of ENTRY_SCALAR_KEYS)
            if (before[key] !== entry[key])
                changes.push({ kind: 'entry-field', index, key, value: entry[key] });
        const stored = new Set(before.valueKeys);
        const add = (0, database_value_summary_1.capDatabaseShardValueKeys)(before.valueKeys, entry.valueKeys).filter(key => !stored.has(key));
        if (add.length)
            changes.push({ kind: 'entry-value-keys', index, add });
        const fingerprints = before.rowFingerprints ?? {};
        const next = entry.rowFingerprints ?? {};
        const set = Object.fromEntries(Object.entries(next).filter(([session, bytes]) => !sameDatabaseBytes(fingerprints[session], bytes)));
        const remove = Object.keys(fingerprints).filter(session => !(session in next));
        if (Object.keys(set).length || remove.length)
            changes.push({ kind: 'entry-row-fingerprints', index, set, remove });
        const clocks = before.rowWriteClocks ?? {};
        const nextClocks = entry.rowWriteClocks ?? {};
        const setClocks = Object.fromEntries(Object.entries(nextClocks).filter(([clientId, clock]) => clocks[clientId] !== clock));
        const removeClocks = Object.keys(clocks).filter(clientId => !(clientId in nextClocks));
        if (Object.keys(setClocks).length || removeClocks.length)
            changes.push({
                kind: 'entry-row-write-clocks',
                index,
                set: setClocks,
                remove: removeClocks,
            });
    });
    return changes;
}
/**
 * Whether two byte arrays hold the same bytes. A plain loop: row fingerprints
 * grow with the rows of a shard and are compared on every write.
 */
function sameDatabaseBytes(a, b) {
    if (!a || a.length !== b.length)
        return false;
    for (let index = 0; index < a.length; index++)
        if (a[index] !== b[index])
            return false;
    return true;
}
/** Stored keys of one shard entry; absent optional fields stay absent. */
function databaseShardManifestEntryFields(entry) {
    return [
        ...ENTRY_SCALAR_KEYS.flatMap((key) => entry[key] === undefined ? [] : [[key, entry[key]]]),
        ...Object.entries(entry.rowFingerprints ?? {}).map(([session, bytes]) => [
            database_row_fingerprint_1.DATABASE_ROW_FINGERPRINT_KEY_PREFIX + session,
            bytes,
        ]),
        ...Object.entries(entry.rowWriteClocks ?? {}).map(([clientId, clock]) => [
            database_row_fingerprint_1.DATABASE_ROW_WRITE_CLOCK_KEY_PREFIX + clientId,
            clock,
        ]),
        ...(entry.valueKeys ?? []).map((key) => [key, true]),
    ];
}
/** Fresh CRDT form of one shard entry. */
function databaseShardManifestEntryToYMap(entry) {
    return new yjs_1.Map(databaseShardManifestEntryFields(entry));
}
/**
 * Applies `changes` of {@link planDatabaseShardManifestChanges} to the map
 * holding the fields and to its `shards` array: the manifest of a block or a
 * directory part.
 */
function applyDatabaseShardManifestChanges(map, shards, changes) {
    for (const change of changes) {
        switch (change.kind) {
            case 'field':
                map.set(change.key, change.value);
                break;
            case 'shards':
                if (change.deleteCount)
                    shards.delete(change.index, change.deleteCount);
                if (change.insert.length)
                    shards.insert(change.index, change.insert.map(databaseShardManifestEntryToYMap));
                break;
            case 'entry-field': {
                const entry = shards.get(change.index);
                if (change.value === undefined)
                    entry.delete(change.key);
                else
                    entry.set(change.key, change.value);
                break;
            }
            case 'entry-row-fingerprints': {
                const entry = shards.get(change.index);
                for (const session of change.remove)
                    entry.delete(database_row_fingerprint_1.DATABASE_ROW_FINGERPRINT_KEY_PREFIX + session);
                for (const [session, bytes] of Object.entries(change.set))
                    entry.set(database_row_fingerprint_1.DATABASE_ROW_FINGERPRINT_KEY_PREFIX + session, bytes);
                break;
            }
            case 'entry-row-write-clocks': {
                const entry = shards.get(change.index);
                for (const clientId of change.remove)
                    entry.delete(database_row_fingerprint_1.DATABASE_ROW_WRITE_CLOCK_KEY_PREFIX + clientId);
                for (const [clientId, clock] of Object.entries(change.set))
                    entry.set(database_row_fingerprint_1.DATABASE_ROW_WRITE_CLOCK_KEY_PREFIX + clientId, clock);
                break;
            }
            case 'entry-value-keys': {
                const entry = shards.get(change.index);
                for (const key of change.add)
                    entry.set(key, true);
                break;
            }
        }
    }
}
